// `@sentry/react` (browser tracing) is a large dependency that is only ever
// active in production and is never needed for first paint. Importing it
// statically pulls the whole SDK into the main/initial chunk, slowing initial
// load for every visitor. Instead we lazy-load it: the SDK is fetched as a
// separate `sentry` chunk the first time monitoring is actually enabled, and
// every facade function below no-ops (when disabled) or buffers (until the SDK
// is ready) so caller behavior is unchanged.
let Sentry = null;
let sentryLoadPromise = null;
let sentryStartupScheduled = false;
const pendingSentryOps = [];
const MAX_PENDING_SENTRY_OPS = 100;

const SENTRY_DSN_DEFAULT =
  "https://a2de2aff6a225ef0534f84387f29437d@o4507930242711552.ingest.us.sentry.io/4508094113513472";
const SENSITIVE_KEY_PATTERN =
  /(authorization|token|password|secret|documenttext|document_text|content|body|html|raw)/i;

const readStringEnv = (value) => (typeof value === "string" ? value.trim() : "");
const readBooleanEnv = (value) => ["1", "true", "yes"].includes(readStringEnv(value).toLowerCase());
const parseNumericEnv = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const sanitizeContext = (value, depth = 0) => {
  if (value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return typeof value === "string" && value.length > 500 ? `${value.slice(0, 497)}...` : value;
  }
  if (depth >= 3) return "[truncated]";
  if (Array.isArray(value)) return value.slice(0, 20).map((item) => sanitizeContext(item, depth + 1));
  if (typeof value === "object") {
    return Object.entries(value).reduce((result, [key, entry]) => {
      if (entry !== undefined && !SENSITIVE_KEY_PATTERN.test(key)) result[key] = sanitizeContext(entry, depth + 1);
      return result;
    }, {});
  }
  return String(value);
};

const getCurrentHostname = () =>
  typeof window === "undefined" ? "" : readStringEnv(window.location?.hostname).toLowerCase();

const isLocalRuntime = () => {
  const hostname = getCurrentHostname();
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname.endsWith(".local");
};

export const getSentryDsn = () =>
  readStringEnv(process.env.REACT_APP_SENTRY_DSN) || SENTRY_DSN_DEFAULT;
export const isSentryDisabled = () => readBooleanEnv(process.env.REACT_APP_SENTRY_DISABLED);
export const getSentryEnvironment = () =>
  readStringEnv(process.env.REACT_APP_SENTRY_ENV) || readStringEnv(process.env.NODE_ENV) || "development";
export const shouldEnableSentry = () => {
  const environment = getSentryEnvironment().toLowerCase();
  return Boolean(getSentryDsn() && !isSentryDisabled() && environment !== "test" && environment !== "development" && !isLocalRuntime());
};
export const isSentryEnabled = () => Boolean(Sentry && shouldEnableSentry() && Sentry.getClient());

export const isIgnorableSentryError = (error) => {
  const message = readStringEnv(
    typeof error === "string"
      ? error
      : error?.message || error?.reason?.message,
  ).toLowerCase();
  // Axios >=1 rejects a cancelled request with a `CanceledError` carrying
  // `code: "ERR_CANCELED"`. Its default message is "canceled", but a caller
  // that passes an abort reason replaces that string, so matching on the
  // message alone let those through as if they were real failures.
  const name = typeof error === "string" ? "" : error?.name || error?.reason?.name;
  const code = readStringEnv(
    typeof error === "string" ? "" : error?.code || error?.reason?.code,
  ).toUpperCase();
  return (
    name === "AbortError" ||
    name === "CanceledError" ||
    code === "ERR_CANCELED" ||
    message === "canceled" ||
    message === "cancelled" ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk")
  );
};

export const isIgnorableSentryEvent = (event) =>
  (event?.exception?.values || []).some(
    (exception) =>
      String(exception?.type || "").toLowerCase() === "axioserror" &&
      String(exception?.value || "").trim().toLowerCase() === "request aborted",
  );

// Initialize the SDK client once it has been dynamically loaded.
const initSentryClient = (S) => {
  if (S.getClient()) return;
  S.init({
    dsn: getSentryDsn(),
    environment: getSentryEnvironment(),
    release: readStringEnv(process.env.REACT_APP_BUILD_ID) || undefined,
    enabled: true,
    tracesSampleRate: parseNumericEnv(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0),
    beforeSend: (event) => (isIgnorableSentryEvent(event) ? null : event),
  });
};

const flushPendingSentryOps = () => {
  if (!Sentry) return;
  const ops = pendingSentryOps.splice(0, pendingSentryOps.length);
  ops.forEach((op) => {
    try {
      op(Sentry);
    } catch (error) {
      void error;
    }
  });
};

// Dynamically import the SDK (once), initialize the client, and drain buffered
// operations. Resolves to the loaded namespace, or null when disabled or the
// chunk fails to load.
const ensureSentryStarted = () => {
  if (!shouldEnableSentry()) return Promise.resolve(null);
  if (Sentry && Sentry.getClient()) return Promise.resolve(Sentry);

  if (!sentryLoadPromise) {
    sentryLoadPromise = import(
      /* webpackChunkName: "sentry" */
      "@sentry/react"
    )
      .then((mod) => {
        Sentry = mod;
        initSentryClient(Sentry);
        flushPendingSentryOps();
        return Sentry;
      })
      .catch((error) => {
        void error;
        sentryLoadPromise = null;
        return null;
      });
  }

  return sentryLoadPromise;
};

const scheduleSentryStartup = () => {
  if (sentryStartupScheduled || !shouldEnableSentry()) return;
  sentryStartupScheduled = true;
  const start = () => {
    sentryStartupScheduled = false;
    ensureSentryStarted();
  };

  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(start, { timeout: 5000 });
  } else if (typeof window !== "undefined") {
    window.setTimeout(start, 2500);
  }
};

// Run `fn(Sentry)` immediately when the SDK is ready, buffer it until the SDK
// finishes loading when monitoring is enabled, or drop it when disabled.
const runWhenSentryReady = (fn, urgent = false) => {
  if (Sentry && isSentryEnabled()) {
    fn(Sentry);
    return;
  }
  if (!shouldEnableSentry()) return;
  if (pendingSentryOps.length < MAX_PENDING_SENTRY_OPS) pendingSentryOps.push(fn);
  if (urgent) ensureSentryStarted();
  else scheduleSentryStartup();
};

export const initializeSentry = () => {
  if (!shouldEnableSentry()) return;
  // Start monitoring when the browser is idle; error captures still load it urgently.
  scheduleSentryStartup();
};

export const setSentryUser = (user) => {
  if (!user) return;
  runWhenSentryReady((S) =>
    S.setUser({
      id: user.cognito_user_id || user.userId || user.id || undefined,
      email: user.email || undefined,
      username: user.username || undefined,
    }),
  );
};
export const clearSentryUser = () => {
  // No user can have been set before the SDK loaded, so skip loading it just to
  // clear nothing.
  if (!Sentry) return;
  runWhenSentryReady((S) => S.setUser(null));
};
export const setSentryTag = (key, value) => {
  if (!key || value == null || value === "") return;
  runWhenSentryReady((S) => S.setTag(key, String(value)));
};
export const setSentryContext = (name, context) => {
  if (!name || !context || typeof context !== "object") return;
  runWhenSentryReady((S) => S.setContext(name, sanitizeContext(context)));
};

export const captureException = (error, options = {}) => {
  if (!error || isIgnorableSentryError(error) || !shouldEnableSentry()) return;

  runWhenSentryReady((S) => {
    S.withScope((scope) => {
      Object.entries(options.tags || {}).forEach(([key, value]) => {
        if (value != null && value !== "") scope.setTag(key, String(value));
      });
      Object.entries(options.contexts || {}).forEach(([name, context]) => {
        if (context && typeof context === "object") {
          scope.setContext(name, sanitizeContext(context));
        }
      });
      if (Array.isArray(options.fingerprint) && options.fingerprint.length > 0) {
        scope.setFingerprint(options.fingerprint.map((part) => String(part).slice(0, 200)));
      }
      if (options.level) scope.setLevel(options.level);
      S.captureException(error);
    });
  }, true);
};
